import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import {
  ORG_BOUNDARY_FOCUS,
  ORG_BOUNDARY_ISSUE,
  ORG_BOUNDARY_OWNERSHIP_ISSUE_PREFIX,
  REPORTING_ENTITY_DESCRIPTION_FIELD_ID,
  REPORTING_ENTITY_DESCRIPTION_MIN_LENGTH,
} from './orgBoundaryFocus';

export {
  OPEN_BOUNDARIES_EDITOR_EVENT,
  ORG_BOUNDARY_FOCUS,
  ORG_BOUNDARY_ISSUE,
  ORG_BOUNDARY_OWNERSHIP_ISSUE_PREFIX,
  REPORTING_ENTITY_DESCRIPTION_FIELD_ID,
  REPORTING_ENTITY_DESCRIPTION_ISSUE,
  REPORTING_ENTITY_DESCRIPTION_MIN_LENGTH,
} from './orgBoundaryFocus';

export type LegalEntityRow = {
  name: string;
  relationshipType: string;
  consolidationBasis: string;
};

export type RelatedPartyRow = {
  name: string;
  ownershipPercent: string;
  operationalControl: boolean;
  country?: string;
};

export type HoldingCompanyDraft = {
  name: string;
  ownershipPercent: string;
  country: string;
  includedInBoundary: boolean;
};

export type OrgBoundaryDraft = {
  consolidationApproach: string;
  reportingEntityDescription: string;
  includedLegalEntities: LegalEntityRow[];
  jointVentureEmissionAllocation: string;
  franchisesOrOutsourcedOperationsTreatment: string;
  nonControlledOperationsExcluded: boolean;
  organizationalBoundaryNotes: string;
  onSiteFuelCombustion?: string;
  fleetControl?: string;
  logisticsArrangement?: string;
  reportingEquitySharePercent?: string;
  singleLegalEntity?: boolean;
  holdingCompany?: HoldingCompanyDraft;
  subsidiaries?: RelatedPartyRow[];
  jointVentures?: RelatedPartyRow[];
};

type OrganizationalBoundaryEaseProps = {
  value: OrgBoundaryDraft;
  onChange: (next: OrgBoundaryDraft) => void;
  disabled?: boolean;
  /** Distinguishes draft readiness from a successful persistence. Default: ready_to_save when no issues. */
  readiness?: OrgBoundaryDraftReadiness;
};

const APPROACHES = [
  {
    value: 'operational_control',
    title: 'We run the plants',
    hint: 'Recommended. Count every site your team operates, even if you do not own all of it.',
    basis: 'operational control',
  },
  {
    value: 'financial_control',
    title: 'We own the finances',
    hint: 'Count sites you steer through budgets and financial policy.',
    basis: 'financial control',
  },
  {
    value: 'equity_share',
    title: 'We count our ownership %',
    hint: 'Count only your share of each joint venture or subsidiary.',
    basis: 'equity share',
  },
] as const;

const SITE_PRESETS = [
  { relationshipType: 'plant', label: 'Our plant' },
  { relationshipType: 'branch', label: 'Branch' },
  { relationshipType: 'subsidiary', label: 'Subsidiary' },
  { relationshipType: 'warehouse', label: 'Warehouse' },
] as const;

const JV_CHOICES = [
  { value: 'not_applicable', label: 'No joint ventures' },
  { value: 'operational_share', label: 'Split by who operates' },
  { value: 'proportional_equity', label: 'Split by ownership %' },
] as const;

function basisFor(approach: string): string {
  return APPROACHES.find((item) => item.value === approach)?.basis || 'operational control';
}

const emptyHolding = (): HoldingCompanyDraft => ({
  name: '',
  ownershipPercent: '',
  country: 'India',
  includedInBoundary: false,
});

const focusTargetSx = {
  scrollMarginTop: '96px',
  borderRadius: 1,
} as const;

export function reportingEntityDescriptionIssue(value: OrgBoundaryDraft): string | null {
  if (value.reportingEntityDescription.trim().length < REPORTING_ENTITY_DESCRIPTION_MIN_LENGTH) {
    return ORG_BOUNDARY_ISSUE.reportingEntity;
  }
  return null;
}

/** Scroll/focus the reporting-entity field when it is in the document. No-op if missing. */
export function focusReportingEntityDescriptionField(): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(REPORTING_ENTITY_DESCRIPTION_FIELD_ID);
  if (!(el instanceof HTMLElement)) return;
  el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  el.focus();
}

export type OrgBoundaryDraftReadiness = 'incomplete' | 'ready_to_save' | 'saved';

export function organizationalBoundaryIssues(value: OrgBoundaryDraft): string[] {
  const issues: string[] = [];
  if (!value.consolidationApproach) {
    issues.push(ORG_BOUNDARY_ISSUE.consolidation);
  }
  const reportingIssue = reportingEntityDescriptionIssue(value);
  if (reportingIssue) {
    issues.push(reportingIssue);
  }
  if (!value.jointVentureEmissionAllocation) {
    issues.push(ORG_BOUNDARY_ISSUE.jvAllocation);
  }
  if (!value.franchisesOrOutsourcedOperationsTreatment.trim()) {
    issues.push(ORG_BOUNDARY_ISSUE.franchises);
  }
  const holding = value.holdingCompany || emptyHolding();
  const subsidiaries = value.subsidiaries || [];
  const jointVentures = value.jointVentures || [];
  const namedRelated = [
    holding.name.trim() ? holding : null,
    ...subsidiaries.filter((row) => row.name.trim()),
    ...jointVentures.filter((row) => row.name.trim()),
  ].filter(Boolean);
  if (!value.singleLegalEntity && namedRelated.length === 0 && value.includedLegalEntities.every((row) => !row.name.trim())) {
    issues.push(ORG_BOUNDARY_ISSUE.singleOrRelated);
  }
  if (!value.onSiteFuelCombustion || value.onSiteFuelCombustion === 'unknown') {
    issues.push(ORG_BOUNDARY_ISSUE.fuel);
  }
  if (!value.fleetControl || value.fleetControl === 'unknown') {
    issues.push(ORG_BOUNDARY_ISSUE.fleet);
  }
  if (!value.logisticsArrangement || value.logisticsArrangement === 'unknown') {
    issues.push(ORG_BOUNDARY_ISSUE.logistics);
  }
  if (value.consolidationApproach === 'equity_share') {
    const share = Number(value.reportingEquitySharePercent);
    if (!Number.isFinite(share) || share <= 0 || share > 100) {
      issues.push(ORG_BOUNDARY_ISSUE.equityPercent);
    }
  }
  if (value.consolidationApproach === 'equity_share') {
    namedRelated.forEach((row) => {
      const pct = Number((row as RelatedPartyRow | HoldingCompanyDraft).ownershipPercent);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
        const name = ((row as HoldingCompanyDraft).name || 'each related party').trim()
          || 'each related party';
        issues.push(`${ORG_BOUNDARY_OWNERSHIP_ISSUE_PREFIX}${name}.`);
      }
    });
  }
  return issues;
}

const OrganizationalBoundaryEase: React.FC<OrganizationalBoundaryEaseProps> = ({
  value,
  onChange,
  disabled = false,
  readiness,
}) => {
  const selected = APPROACHES.find((item) => item.value === value.consolidationApproach) || APPROACHES[0];
  const holding = value.holdingCompany || emptyHolding();
  const subsidiaries = value.subsidiaries || [];
  const jointVentures = value.jointVentures || [];
  const issues = organizationalBoundaryIssues(value);
  const reportingEntityError = reportingEntityDescriptionIssue(value);
  const resolvedReadiness: OrgBoundaryDraftReadiness = readiness
    || (issues.length === 0 ? 'ready_to_save' : 'incomplete');

  const addSite = (relationshipType: string, label: string) => {
    onChange({
      ...value,
      includedLegalEntities: [
        ...value.includedLegalEntities,
        { name: label, relationshipType, consolidationBasis: basisFor(value.consolidationApproach) },
      ],
    });
  };

  return (
    <Stack spacing={2} data-testid="org-boundary-ease">
      <Box
        component="figure"
        id={ORG_BOUNDARY_FOCUS.consolidation}
        data-testid={ORG_BOUNDARY_FOCUS.consolidation}
        data-my-company-focus-target=""
        tabIndex={-1}
        sx={{
          ...focusTargetSx,
          m: 0,
          p: { xs: 1.5, md: 2 },
          border: '2px dashed',
          borderColor: '#0f766e',
          borderRadius: 2,
          bgcolor: (theme) => alpha('#ecfdf5', theme.palette.mode === 'dark' ? 0.08 : 0.65),
        }}
        aria-label="Which operations sit inside your company boundary"
      >
        <Typography component="figcaption" variant="subtitle2" sx={{ fontWeight: 800, color: '#0f766e' }}>
          Inside your company
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {selected.hint} Suppliers and contractors stay outside unless you add them.
        </Typography>
        <Grid container spacing={1.5}>
          {APPROACHES.map((approach) => {
            const active = value.consolidationApproach === approach.value;
            return (
              <Grid item xs={12} md={4} key={approach.value}>
                <Box
                  component="button"
                  type="button"
                  disabled={disabled}
                  data-testid={`org-approach-${approach.value}`}
                  aria-pressed={active}
                  onClick={() => onChange({
                    ...value,
                    consolidationApproach: approach.value,
                    includedLegalEntities: value.includedLegalEntities.map((row) => ({
                      ...row,
                      consolidationBasis: approach.basis,
                    })),
                  })}
                  sx={{
                    width: '100%',
                    height: '100%',
                    textAlign: 'left',
                    cursor: disabled ? 'default' : 'pointer',
                    borderRadius: 2,
                    p: 1.5,
                    border: '2px solid',
                    borderColor: active ? '#0f766e' : 'divider',
                    bgcolor: active ? alpha('#0f766e', 0.1) : 'background.paper',
                    color: 'text.primary',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {approach.title}
                    </Typography>
                    {approach.value === 'operational_control' ? (
                      <Chip size="small" label="Usual" color="success" sx={{ height: 20, fontWeight: 700 }} />
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                    {approach.hint}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Box
        id={ORG_BOUNDARY_FOCUS.sites}
        data-testid={ORG_BOUNDARY_FOCUS.sites}
        data-my-company-focus-target=""
        tabIndex={-1}
        sx={focusTargetSx}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
          Sites we count
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Tap a site type. Rename it if you have more than one.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {SITE_PRESETS.map((preset) => (
            <Chip
              key={preset.relationshipType}
              icon={<AddCircleOutline />}
              label={preset.label}
              onClick={() => addSite(preset.relationshipType, preset.label)}
              disabled={disabled}
              variant="outlined"
              data-testid={`org-site-preset-${preset.relationshipType}`}
            />
          ))}
        </Stack>
        <Stack spacing={1}>
          {value.includedLegalEntities.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No extra sites yet. Your main company is already inside the boundary.
            </Typography>
          ) : null}
          {value.includedLegalEntities.map((row, index) => (
            <Grid container spacing={1} key={`legal-entity-${index}`} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Site name"
                  value={row.name}
                  disabled={disabled}
                  onChange={(event) => {
                    const next = [...value.includedLegalEntities];
                    next[index] = { ...next[index], name: event.target.value };
                    onChange({ ...value, includedLegalEntities: next });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {row.relationshipType || 'site'} · {row.consolidationBasis || selected.basis}
                </Typography>
              </Grid>
              <Grid item xs={12} md={1}>
                <IconButton
                  aria-label="Remove site"
                  disabled={disabled}
                  onClick={() => onChange({
                    ...value,
                    includedLegalEntities: value.includedLegalEntities.filter((_, i) => i !== index),
                  })}
                >
                  <DeleteOutline />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>
      </Box>

      <Box
        id={ORG_BOUNDARY_FOCUS.reportingEntityGroup}
        data-testid={ORG_BOUNDARY_FOCUS.reportingEntityGroup}
        data-my-company-focus-target=""
        sx={focusTargetSx}
      >
        <TextField
          fullWidth
          required
          size="small"
          id={REPORTING_ENTITY_DESCRIPTION_FIELD_ID}
          label="Reporting entity for this inventory"
          value={value.reportingEntityDescription}
          disabled={disabled}
          error={Boolean(reportingEntityError)}
          onChange={(event) => onChange({ ...value, reportingEntityDescription: event.target.value })}
          placeholder="Company name and sites in this inventory"
          helperText={(
            <span data-testid="org-reporting-entity-helper">
              {reportingEntityError
                || `Required. Name your company and the sites counted in this inventory (at least ${REPORTING_ENTITY_DESCRIPTION_MIN_LENGTH} characters after trimming). Notes below are optional and do not replace this description.`}
            </span>
          )}
          FormHelperTextProps={{
            id: `${REPORTING_ENTITY_DESCRIPTION_FIELD_ID}-helper`,
          }}
          inputProps={{
            'data-testid': REPORTING_ENTITY_DESCRIPTION_FIELD_ID,
            'aria-invalid': Boolean(reportingEntityError),
            'aria-describedby': `${REPORTING_ENTITY_DESCRIPTION_FIELD_ID}-helper`,
          }}
        />
      </Box>

      <Box
        id={ORG_BOUNDARY_FOCUS.relatedParties}
        data-testid={ORG_BOUNDARY_FOCUS.relatedParties}
        data-my-company-focus-target=""
        tabIndex={-1}
        sx={focusTargetSx}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Organizational boundary — holding company, subsidiaries, joint ventures
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          GHG Protocol Corporate Standard consolidation. Required before the inventory is used in calculations.
        </Typography>
        <FormControlLabel
          control={(
            <Checkbox
              checked={Boolean(value.singleLegalEntity)}
              disabled={disabled}
              onChange={(event) => onChange({ ...value, singleLegalEntity: event.target.checked })}
              inputProps={{
                id: ORG_BOUNDARY_FOCUS.singleLegalEntity,
                'data-testid': ORG_BOUNDARY_FOCUS.singleLegalEntity,
              } as React.InputHTMLAttributes<HTMLInputElement>}
            />
          )}
          label="Single legal entity — no holding company, subsidiaries, or joint ventures"
        />
        <Box
          id={ORG_BOUNDARY_FOCUS.holding}
          data-testid={ORG_BOUNDARY_FOCUS.holding}
          data-my-company-focus-target=""
          sx={{ ...focusTargetSx, mt: 0.5 }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Holding company"
                value={holding.name}
                disabled={disabled}
                onChange={(event) => onChange({
                  ...value,
                  holdingCompany: { ...holding, name: event.target.value },
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField
                fullWidth
                size="small"
                label="Ownership %"
                value={holding.ownershipPercent}
                disabled={disabled}
                inputProps={{ 'data-testid': 'org-holding-ownership-percent' }}
                onChange={(event) => onChange({
                  ...value,
                  holdingCompany: { ...holding, ownershipPercent: event.target.value },
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Country"
                value={holding.country}
                disabled={disabled}
                onChange={(event) => onChange({
                  ...value,
                  holdingCompany: { ...holding, country: event.target.value },
                })}
              />
            </Grid>
          </Grid>
        </Box>
        <Box
          id={ORG_BOUNDARY_FOCUS.relatedOwnership}
          data-testid={ORG_BOUNDARY_FOCUS.relatedOwnership}
          data-my-company-focus-target=""
          tabIndex={-1}
          sx={focusTargetSx}
        >
          <Box
            id={ORG_BOUNDARY_FOCUS.subsidiaries}
            data-testid={ORG_BOUNDARY_FOCUS.subsidiaries}
            data-my-company-focus-target=""
            tabIndex={-1}
            sx={focusTargetSx}
          >
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>Subsidiaries</Typography>
            {subsidiaries.map((row, index) => (
              <Grid container spacing={1} key={`subsidiary-${index}`} sx={{ mt: 0.5 }} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Subsidiary name"
                    value={row.name}
                    disabled={disabled}
                    onChange={(event) => {
                      const next = [...subsidiaries];
                      next[index] = { ...next[index], name: event.target.value };
                      onChange({ ...value, subsidiaries: next });
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ownership %"
                    value={row.ownershipPercent}
                    disabled={disabled}
                    onChange={(event) => {
                      const next = [...subsidiaries];
                      next[index] = { ...next[index], ownershipPercent: event.target.value };
                      onChange({ ...value, subsidiaries: next });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <IconButton
                    aria-label="Remove subsidiary"
                    disabled={disabled}
                    onClick={() => onChange({
                      ...value,
                      subsidiaries: subsidiaries.filter((_, i) => i !== index),
                    })}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              size="small"
              startIcon={<AddCircleOutline />}
              disabled={disabled}
              data-testid="org-add-subsidiary"
              onClick={() => onChange({
                ...value,
                subsidiaries: [...subsidiaries, { name: '', ownershipPercent: '100', operationalControl: true }],
              })}
            >
              Add subsidiary
            </Button>
          </Box>
          <Box
            id={ORG_BOUNDARY_FOCUS.jointVentures}
            data-testid={ORG_BOUNDARY_FOCUS.jointVentures}
            data-my-company-focus-target=""
            tabIndex={-1}
            sx={focusTargetSx}
          >
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>
              Joint ventures — equity share or operational control
            </Typography>
            <Box
              id={ORG_BOUNDARY_FOCUS.jvAllocation}
              data-testid={ORG_BOUNDARY_FOCUS.jvAllocation}
              data-my-company-focus-target=""
              tabIndex={-1}
              sx={{ ...focusTargetSx, my: 1 }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {JV_CHOICES.map((choice) => (
                  <Chip
                    key={choice.value}
                    label={choice.label}
                    color={value.jointVentureEmissionAllocation === choice.value ? 'primary' : 'default'}
                    variant={value.jointVentureEmissionAllocation === choice.value ? 'filled' : 'outlined'}
                    onClick={() => onChange({ ...value, jointVentureEmissionAllocation: choice.value })}
                    disabled={disabled}
                    data-testid={`org-jv-allocation-${choice.value}`}
                  />
                ))}
              </Stack>
            </Box>
            {jointVentures.map((row, index) => (
              <Grid container spacing={1} key={`jv-${index}`} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Joint venture"
                    value={row.name}
                    disabled={disabled}
                    onChange={(event) => {
                      const next = [...jointVentures];
                      next[index] = { ...next[index], name: event.target.value };
                      onChange({ ...value, jointVentures: next });
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ownership %"
                    value={row.ownershipPercent}
                    disabled={disabled}
                    onChange={(event) => {
                      const next = [...jointVentures];
                      next[index] = { ...next[index], ownershipPercent: event.target.value };
                      onChange({ ...value, jointVentures: next });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <IconButton
                    aria-label="Remove joint venture"
                    disabled={disabled}
                    onClick={() => onChange({
                      ...value,
                      jointVentures: jointVentures.filter((_, i) => i !== index),
                    })}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              size="small"
              startIcon={<AddCircleOutline />}
              disabled={disabled}
              data-testid="org-add-joint-venture"
              onClick={() => onChange({
                ...value,
                jointVentures: [...jointVentures, { name: '', ownershipPercent: '', operationalControl: false }],
              })}
            >
              Add joint venture
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        id={ORG_BOUNDARY_FOCUS.franchises}
        data-testid={ORG_BOUNDARY_FOCUS.franchises}
        data-my-company-focus-target=""
        sx={focusTargetSx}
      >
        <TextField
          fullWidth
          required
          size="small"
          multiline
          minRows={2}
          id={`${ORG_BOUNDARY_FOCUS.franchises}-input`}
          label="Franchises and outsourced operations"
          value={value.franchisesOrOutsourcedOperationsTreatment}
          disabled={disabled}
          onChange={(event) => onChange({
            ...value,
            franchisesOrOutsourcedOperationsTreatment: event.target.value,
          })}
          placeholder="None, or name the outlets inside the operational boundary"
          helperText="Required. GHG Protocol treatment of franchises and outsourced operations."
          inputProps={{ 'data-testid': `${ORG_BOUNDARY_FOCUS.franchises}-input` }}
        />
      </Box>
      <FormControlLabel
        control={(
          <Checkbox
            checked={value.nonControlledOperationsExcluded}
            disabled={disabled}
            onChange={(event) => onChange({
              ...value,
              nonControlledOperationsExcluded: event.target.checked,
            })}
          />
        )}
        label="Exclude operations without operational or financial control"
      />
      <Box data-testid="org-scope-segregation">
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
          What the company operates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          These answers split fuel and freight bills into Scope 1 (you operate the equipment) or Scope 3 (a contractor does).
        </Typography>
        <Box
          id={ORG_BOUNDARY_FOCUS.fuel}
          data-testid={ORG_BOUNDARY_FOCUS.fuel}
          data-my-company-focus-target=""
          tabIndex={-1}
          sx={{ ...focusTargetSx, mb: 1 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Fuel burned in equipment you operate</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {[
              ['yes', 'Yes, on our sites'],
              ['no', 'No, we do not burn fuel'],
            ].map(([choice, label]) => (
              <Chip
                key={choice}
                label={label}
                color={value.onSiteFuelCombustion === choice ? 'primary' : 'default'}
                variant={value.onSiteFuelCombustion === choice ? 'filled' : 'outlined'}
                disabled={disabled}
                data-testid={`org-fuel-${choice}`}
                onClick={() => onChange({ ...value, onSiteFuelCombustion: choice })}
              />
            ))}
          </Stack>
        </Box>
        <Box
          id={ORG_BOUNDARY_FOCUS.fleet}
          data-testid={ORG_BOUNDARY_FOCUS.fleet}
          data-my-company-focus-target=""
          tabIndex={-1}
          sx={{ ...focusTargetSx, mb: 1 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Vehicles the company operates</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {[
              ['owned_or_leased', 'We operate vehicles'],
              ['no_fleet', 'No company vehicles'],
              ['mixed', 'Some of each'],
            ].map(([choice, label]) => (
              <Chip
                key={choice}
                label={label}
                color={value.fleetControl === choice ? 'primary' : 'default'}
                variant={value.fleetControl === choice ? 'filled' : 'outlined'}
                disabled={disabled}
                data-testid={`org-fleet-${choice}`}
                onClick={() => onChange({ ...value, fleetControl: choice })}
              />
            ))}
          </Stack>
        </Box>
        <Box
          id={ORG_BOUNDARY_FOCUS.logistics}
          data-testid={ORG_BOUNDARY_FOCUS.logistics}
          data-my-company-focus-target=""
          tabIndex={-1}
          sx={focusTargetSx}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Who moves goods</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {[
              ['company_operated', 'Our vehicles'],
              ['contracted', 'Contractors'],
              ['both', 'Both'],
            ].map(([choice, label]) => (
              <Chip
                key={choice}
                label={label}
                color={value.logisticsArrangement === choice ? 'primary' : 'default'}
                variant={value.logisticsArrangement === choice ? 'filled' : 'outlined'}
                disabled={disabled}
                data-testid={`org-logistics-${choice}`}
                onClick={() => onChange({ ...value, logisticsArrangement: choice })}
              />
            ))}
          </Stack>
        </Box>
      </Box>
      {value.consolidationApproach === 'equity_share' ? (
        <Box
          id="org-boundary-equity-percent-group"
          data-testid="org-boundary-equity-percent-group"
          data-my-company-focus-target=""
          sx={focusTargetSx}
        >
          <TextField
            fullWidth
            required
            size="small"
            type="number"
            id={ORG_BOUNDARY_FOCUS.equityPercent}
            label="Equity share counted in this inventory (%)"
            value={value.reportingEquitySharePercent || ''}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, reportingEquitySharePercent: event.target.value })}
            helperText="GHG Protocol equity share. 40 counts 40% of each activity."
            inputProps={{ min: 1, max: 100, 'data-testid': ORG_BOUNDARY_FOCUS.equityPercent }}
          />
        </Box>
      ) : null}
      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Organizational boundary notes"
        value={value.organizationalBoundaryNotes}
        disabled={disabled}
        onChange={(event) => onChange({ ...value, organizationalBoundaryNotes: event.target.value })}
        helperText={(
          <span data-testid="org-boundary-notes-helper">
            Optional. Notes do not replace the required reporting-entity description above.
          </span>
        )}
        inputProps={{ 'data-testid': 'org-boundary-notes' }}
      />
      {resolvedReadiness === 'incomplete' ? (
        <Alert
          severity="warning"
          data-testid="org-boundary-draft-status"
          action={
            reportingEntityError ? (
              <Button
                color="inherit"
                size="small"
                type="button"
                onClick={() => focusReportingEntityDescriptionField()}
                data-testid="org-boundary-focus-reporting-entity"
              >
                Go to field
              </Button>
            ) : undefined
          }
        >
          {issues[0]}
        </Alert>
      ) : resolvedReadiness === 'saved' ? (
        <Alert severity="success" data-testid="org-boundary-draft-status">
          Saved to your profile. Home marks Boundaries complete only from this persisted data.
        </Alert>
      ) : (
        <Alert severity="info" data-testid="org-boundary-draft-status">
          Ready to save. Home marks Boundaries complete only after you save successfully.
        </Alert>
      )}

      <Button
        variant="text"
        size="small"
        startIcon={<AddCircleOutline />}
        disabled={disabled}
        onClick={() => addSite('other', '')}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add another site
      </Button>
    </Stack>
  );
};

export default OrganizationalBoundaryEase;
